import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Field as UiField } from "../ui";
import { AuthCover, AuthHeading, PasswordInput } from "./AuthCover";
import { resetPassword } from "../../shared/_services/auth.services";
import { clearResetToken, getResetToken } from "../../shared/_helpers/otpChallenge";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const fromPage = location.state?.from;
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'))?.user;
    if (!user && !fromPage) {
      navigate('/login');
    }
  }, []);

  const handleResetPassword = async (values) => {
    let email = localStorage.getItem("resetPasswordEmail");

    // Backend SEC-084: this route now demands the single-use reset token, which
    // /otp-verify minted when the code was redeemed with
    // `purpose: "reset_password"`. Reaching this screen used to be proof enough
    // of the OTP step — it was enforced only by this app's navigation, so an
    // anonymous caller who knew an email address could set that account's
    // password. Without the token the request is refused, so bounce back to the
    // start of the flow rather than sending one that cannot succeed.
    const token = getResetToken();
    if (!token) {
      enqueueSnackbar("This password reset link has expired. Please request a new code.", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate("/forgot-password");
      return;
    }

    const data = await resetPassword({ email: email, password: values.newPassword, token });
    if (data.status === 200) {
      enqueueSnackbar(data?.data?.message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      // Consumed server-side on success; drop our copy with the email.
      clearResetToken();
      window.localStorage.removeItem("resetPasswordEmail")
      navigate("/login");
    }
    else {
      enqueueSnackbar(
        data.data?.message || data.response?.data?.message || "Password Reset Failed.",
        { variant: "error" }
      );
    }
  };

  const initialValues = {
    newPassword: "",
    confirmNewPassword: "",
  };

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required("New Password is required")
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
        "Password should contain at least 1 number, 1 lowercase, 1 uppercase, 1 special character"
      ),
    confirmNewPassword: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  return (
    <AuthCover
      logoTo="/"
      art={
        <div className="tw-grid tw-gap-4">
          <h2 className="tw-m-0 tw-max-w-sm tw-text-3xl tw-font-semibold tw-text-text">
            Reset Your Password
          </h2>
          <p className="tw-m-0 tw-max-w-md tw-text-text">
            Enter your new password below to securely update your account and regain access to your financial dashboard.
          </p>
          <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-text max-rail:tw-hidden">
            PROFESSIONAL FINANCIAL ADVISORY</p>
        </div>
      }
    >
      <AuthHeading title="Reset Password" />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await handleResetPassword(values);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, touched, errors }) => (
          <Form>
            <UiField label="Enter New Password" error={touched.newPassword && errors.newPassword}>
              <Field
                as={PasswordInput}
                id="newPassword"
                name="newPassword"
                placeholder="Enter New Password"
                shown={showPassword}
                onToggle={() => { setShowPassword(!showPassword) }}
              />
            </UiField>
            <UiField label="Re-enter New Password" error={touched.confirmNewPassword && errors.confirmNewPassword}>
              <Field
                as={PasswordInput}
                id="confirmNewPassword"
                name="confirmNewPassword"
                placeholder="Re-enter New Password"
                shown={showConfirmPassword}
                onToggle={() => { setShowConfirmPassword(!showConfirmPassword) }}
              />
            </UiField>
            <Button type="submit" className="tw-mb-2 tw-mt-2 tw-w-full" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Reset Password"}
            </Button>
          </Form>
        )}
      </Formik>
    </AuthCover>
  );
};

export default ResetPassword;
